import React, { useEffect, useState } from "react";
import RentalProgressIndicator from "./RentalProgressIndicator";
import { apiFetch } from "../../utils/apiFetch";

import "./global/global.css";

const API_BASE_URL = process.env.REACT_APP_PAYVIEW_BASE_URL;

const STATUS_STYLES = {
  pending: { backgroundColor: "#fff3e0", color: "#e65100", label: "Pending" },
  listed: { backgroundColor: "#e8f5e9", color: "#2e7d32", label: "Listed" },
  unlisted: { backgroundColor: "#f5f5f5", color: "#616161", label: "Unlisted" },
};

const EDITABLE_UNIT_FIELDS = [
  "monthly_rent",
  "bedrooms",
  "bathrooms",
  "marketing_title",
  "availability_date",
  "listing_status",
  "progress",
];

const NUMERIC_FIELDS = new Set(["monthly_rent", "bedrooms", "bathrooms"]);

const buttonStyle = {
  border: "1px solid #28a382",
  borderRadius: "4px",
  background: "#fff",
  color: "#1e8968",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 600,
  padding: "8px 14px",
};

const dangerButtonStyle = {
  ...buttonStyle,
  borderColor: "#dc3545",
  color: "#dc3545",
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "None";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const getAddress = (property) => {
  const lines = [property.address_line_1, property.address_line_2, property.address_line_3]
    .filter(Boolean)
    .join(", ");
  const unit = property.address_unit ? ` #${property.address_unit}` : "";
  const locality = [property.address_city, property.address_state, property.address_zip]
    .filter(Boolean)
    .join(", ");
  return { primary: `${lines}${unit}`, secondary: locality };
};

const ListingStatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || {
    backgroundColor: "#f5f5f5",
    color: "#616161",
    label: displayValue(status),
  };
  return (
    <span
      aria-label={`Listing status: ${style.label}`}
      style={{
        backgroundColor: style.backgroundColor,
        borderRadius: "999px",
        color: style.color,
        display: "inline-block",
        fontSize: "12px",
        fontWeight: 600,
        padding: "5px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {style.label}
    </span>
  );
};

const DetailField = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        color: "#6b7280",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.04em",
        marginBottom: "4px",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div style={{ color: "#1f2937", fontSize: "14px", overflowWrap: "anywhere" }}>
      {displayValue(value)}
    </div>
  </div>
);

const DetailSection = ({ title, children }) => (
  <section style={{ marginTop: "22px" }}>
    <h4
      style={{
        borderBottom: "1px solid #e5e7eb",
        color: "#1f2937",
        fontSize: "15px",
        margin: "0 0 14px",
        paddingBottom: "8px",
      }}
    >
      {title}
    </h4>
    {children}
  </section>
);

const DetailGrid = ({ children }) => (
  <div
    style={{
      display: "grid",
      gap: "16px 24px",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    }}
  >
    {children}
  </div>
);

const RentalPhotoImage = ({ photo }) => {
  const [source, setSource] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    const contentUrl = `${API_BASE_URL}${photo.photo_url}`;

    setSource(null);
    setFailed(false);
    apiFetch(contentUrl)
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load rental photo");
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setSource(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.photo_url]);

  if (failed) {
    return (
      <div
        role="img"
        aria-label={`${photo.description || "Rental property photo"} unavailable`}
        style={{
          alignItems: "center",
          background: "#eef3f1",
          color: "#6b7280",
          display: "flex",
          fontSize: "12px",
          height: "110px",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        Photo unavailable
      </div>
    );
  }

  return source ? (
    <img
      src={source}
      alt={photo.description || "Rental property photo"}
      style={{
        background: "#eef3f1",
        border: photo.is_primary ? "3px solid #28a382" : "1px solid #d9e2df",
        borderRadius: "5px",
        display: "block",
        height: "110px",
        objectFit: "cover",
        width: "100%",
      }}
    />
  ) : (
    <div aria-label="Loading rental property photo" style={{ background: "#eef3f1", height: "110px" }} />
  );
};


const RentalDetailPanel = ({
  detail,
  editing,
  editForm,
  saving,
  onEdit,
  onCancel,
  onChange,
  onSave,
  onDelete,
}) => {
  const property = detail.property || {};
  const address = getAddress(property);
  const units = detail.units || [];
  const amenities = detail.amenities || {};

  return (
    <div
      style={{
        background: "#fafdfc",
        border: "1px solid #d8eee7",
        borderRadius: "6px",
        padding: "22px",
      }}
    >
      <div
        style={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          justifyContent: "space-between",
        }}
      >
        <div>
          <h3 style={{ color: "#1f2937", fontSize: "19px", margin: 0 }}>Property details</h3>
          <p style={{ color: "#6b7280", fontSize: "13px", margin: "5px 0 0" }}>
            Review the extracted listing data and uploaded photos.
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {!editing && (
            <button type="button" onClick={onEdit} style={buttonStyle}>
              Edit
            </button>
          )}
          <button type="button" onClick={onDelete} style={dangerButtonStyle}>
            Delete
          </button>
        </div>
      </div>

      {editing ? (
        <EditForm
          editForm={editForm}
          saving={saving}
          onCancel={onCancel}
          onChange={onChange}
          onSave={onSave}
        />
      ) : null}

      <DetailSection title="Photos">
        {detail.photos && detail.photos.length ? (
          <div
            style={{
              display: "grid",
              gap: "12px",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            }}
          >
            {detail.photos.map((photo) => (
              <figure key={photo.id} style={{ margin: 0 }}>
                <RentalPhotoImage photo={photo} />
                <figcaption style={{ color: "#6b7280", fontSize: "12px", marginTop: "5px" }}>
                  {photo.description || (photo.is_primary ? "Primary photo" : "Property photo")}
                </figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>No photos uploaded.</p>
        )}
      </DetailSection>

      <DetailSection title="Property information">
        <DetailGrid>
          <DetailField label="Address" value={address.primary} />
          <DetailField label="City / State / ZIP" value={address.secondary} />
          <DetailField label="Property type" value={property.property_type?.replace(/_/g, " ")} />
          <DetailField label="Property name" value={property.property_name} />
          <DetailField label="Year built" value={property.year_built} />
          <DetailField label="In HOA" value={property.is_in_hoa} />
          <DetailField label="Owner" value={property.property_owner_name} />
          <DetailField label="Owner email" value={property.property_owner_email} />
          <DetailField label="Owner phone" value={property.property_owner_phone} />
          <DetailField label="Available date" value={formatDate(property.available_date)} />
          <DetailField label="Description" value={property.description} />
        </DetailGrid>
      </DetailSection>

      <DetailSection title={`Units (${units.length})`}>
        {units.length ? (
          <div style={{ display: "grid", gap: "16px" }}>
            {units.map((unit) => (
              <div
                key={unit.id}
                style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "5px", padding: "16px" }}
              >
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    justifyContent: "space-between",
                    marginBottom: "14px",
                  }}
                >
                  <strong style={{ color: "#1f2937", fontSize: "15px" }}>
                    {unit.unit_number ? `Unit ${unit.unit_number}` : "Main unit"}
                  </strong>
                  <ListingStatusBadge status={unit.listing_status} />
                </div>
                <DetailGrid>
                  <DetailField label="Monthly rent" value={formatCurrency(unit.monthly_rent)} />
                  <DetailField label="Bedrooms" value={unit.bedrooms} />
                  <DetailField label="Bathrooms" value={unit.bathrooms} />
                  <DetailField label="Square feet" value={unit.square_feet} />
                  <DetailField label="Availability date" value={formatDate(unit.availability_date)} />
                  <DetailField label="Progress" value={unit.progress} />
                  <DetailField label="Marketing title" value={unit.marketing_title} />
                  <DetailField label="Lease" value={unit.lease_term_type} />
                  <DetailField label="Lease duration" value={unit.lease_duration_months} />
                  <DetailField label="Security deposit" value={formatCurrency(unit.security_deposit)} />
                  <DetailField label="Application fee" value={formatCurrency(unit.application_fee)} />
                  <DetailField label="Pet deposit" value={formatCurrency(unit.pet_deposit)} />
                  <DetailField label="Pet fee" value={formatCurrency(unit.pet_fee)} />
                  <DetailField label="Parking fee" value={formatCurrency(unit.parking_fee)} />
                  <DetailField label="Admin fee" value={formatCurrency(unit.admin_fee)} />
                  <DetailField label="Description" value={unit.unit_description} />
                  <DetailField label="Utilities included" value={unit.utilities_included} />
                  <DetailField label="Resident qualifications" value={unit.minimum_resident_qualifications} />
                </DetailGrid>
                <NestedRecords unit={unit} />
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>No units found.</p>
        )}
      </DetailSection>

      <DetailSection title="Amenities">
        {detail.amenities ? (
          <DetailGrid>
            {Object.entries(amenities).map(([key, value]) => (
              <DetailField key={key} label={key.replace(/_/g, " ")} value={value} />
            ))}
          </DetailGrid>
        ) : (
          <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>No amenities recorded.</p>
        )}
      </DetailSection>

    </div>
  );
};

const NestedRecords = ({ unit }) => (
  <div
    style={{
      borderTop: "1px solid #eef1f0",
      display: "grid",
      gap: "16px",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      marginTop: "16px",
      paddingTop: "14px",
    }}
  >
    <div>
      <strong style={{ color: "#374151", display: "block", fontSize: "13px", marginBottom: "8px" }}>
        Fees
      </strong>
      {unit.fees && unit.fees.length ? (
        unit.fees.map((fee) => (
          <div key={fee.id} style={{ color: "#4b5563", fontSize: "13px", marginBottom: "5px" }}>
            {fee.fee_name}: {formatCurrency(fee.amount)} (
            {displayValue(fee.fee_category).replace(/_/g, " ")})
          </div>
        ))
      ) : (
        <span style={{ color: "#6b7280", fontSize: "13px" }}>None</span>
      )}
    </div>
    <div>
      <strong style={{ color: "#374151", display: "block", fontSize: "13px", marginBottom: "8px" }}>
        Utilities
      </strong>
      {unit.utilities ? (
        <div style={{ color: "#4b5563", fontSize: "13px" }}>
          {Object.entries(unit.utilities)
            .filter(([, value]) => value)
            .map(([key]) => key.replace(/_/g, " "))
            .join(", ") || "None included"}
        </div>
      ) : (
        <span style={{ color: "#6b7280", fontSize: "13px" }}>None recorded</span>
      )}
    </div>
  </div>
);

const EditForm = ({ editForm, saving, onCancel, onChange, onSave }) => (
  <form
    onSubmit={onSave}
    style={{ background: "#fff", border: "1px solid #d8eee7", borderRadius: "5px", marginTop: "18px", padding: "16px" }}
  >
    <h4 style={{ color: "#1f2937", fontSize: "15px", margin: "0 0 14px" }}>Correct listing data</h4>
    <div style={{ display: "grid", gap: "14px 18px", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
      <EditInput
        label="Monthly rent"
        name="monthly_rent"
        type="number"
        value={editForm.monthly_rent}
        onChange={onChange}
      />
      <EditInput label="Bedrooms" name="bedrooms" type="number" step="0.1" value={editForm.bedrooms} onChange={onChange} />
      <EditInput label="Bathrooms" name="bathrooms" type="number" step="0.25" value={editForm.bathrooms} onChange={onChange} />
      <EditInput
        label="Availability date"
        name="availability_date"
        type="date"
        value={editForm.availability_date}
        onChange={onChange}
      />
      <EditSelect
        label="Listing status"
        name="listing_status"
        value={editForm.listing_status}
        onChange={onChange}
        options={[
          ["pending", "Pending"],
          ["listed", "Listed"],
          ["unlisted", "Unlisted"],
        ]}
      />
      <EditSelect
        label="Progress"
        name="progress"
        value={editForm.progress}
        onChange={onChange}
        options={[
          ["submitted", "Submitted"],
          ["listed", "Listed"],
          ["tracking", "Tracking"],
          ["closed", "Closed"],
        ]}
      />
      <EditInput
        label="Marketing title"
        name="marketing_title"
        value={editForm.marketing_title}
        onChange={onChange}
      />
      <label style={{ color: "#374151", display: "flex", flexDirection: "column", fontSize: "13px", gap: "6px", gridColumn: "1 / -1" }}>
        Property description
        <textarea
          aria-label="Property description"
          name="description"
          value={editForm.description}
          onChange={onChange}
          rows="3"
          style={{ border: "1px solid #cbd5d1", borderRadius: "4px", font: "inherit", padding: "8px", resize: "vertical" }}
        />
      </label>
    </div>
    <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
      <button type="submit" disabled={saving} style={buttonStyle}>
        {saving ? "Saving..." : "Save"}
      </button>
      <button type="button" disabled={saving} onClick={onCancel} style={{ ...buttonStyle, borderColor: "#9ca3af", color: "#4b5563" }}>
        Cancel
      </button>
    </div>
  </form>
);

const EditInput = ({ label, name, type = "text", step, value, onChange }) => (
  <label style={{ color: "#374151", display: "flex", flexDirection: "column", fontSize: "13px", gap: "6px" }}>
    {label}
    <input
      aria-label={label}
      name={name}
      type={type}
      step={step}
      value={value}
      onChange={onChange}
      style={{ border: "1px solid #cbd5d1", borderRadius: "4px", font: "inherit", minHeight: "34px", padding: "6px 8px" }}
    />
  </label>
);

const EditSelect = ({ label, name, value, onChange, options }) => (
  <label style={{ color: "#374151", display: "flex", flexDirection: "column", fontSize: "13px", gap: "6px" }}>
    {label}
    <select
      aria-label={label}
      name={name}
      value={value}
      onChange={onChange}
      style={{ background: "#fff", border: "1px solid #cbd5d1", borderRadius: "4px", font: "inherit", minHeight: "34px", padding: "6px 8px" }}
    >
      {options.map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>
          {optionLabel}
        </option>
      ))}
    </select>
  </label>
);

const RentalPropertyList = () => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editOriginal, setEditOriginal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/api/rentals/`);
        if (!response.ok) throw new Error("Failed to fetch rental properties");
        const data = await response.json();
        setRentals(data.rentals || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchDetail = async (propertyId) => {
    setDetailLoadingId(propertyId);
    setDetailError(null);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/rentals/${propertyId}/`);
      if (!response.ok) throw new Error("Failed to fetch rental property details");
      const data = await response.json();
      setDetails((current) => ({ ...current, [propertyId]: data }));
    } catch (err) {
      setDetailError({ propertyId, message: err.message });
    } finally {
      setDetailLoadingId(null);
    }
  };

  const handleRowClick = (propertyId) => {
    if (expandedId === propertyId) {
      setExpandedId(null);
      setEditing(false);
      setEditForm(null);
      setEditOriginal(null);
      return;
    }
    setExpandedId(propertyId);
    setEditing(false);
    setEditForm(null);
    setEditOriginal(null);
    if (!details[propertyId]) fetchDetail(propertyId);
  };

  const handleDelete = async (event, propertyId, address) => {
    event.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${address}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/rentals/${propertyId}/`, { method: "DELETE" });
      if (!response.ok) {
        let errorMessage = "Failed to delete property";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `Server error (${response.status})`;
        }
        throw new Error(errorMessage);
      }
      setRentals((current) => current.filter((rental) => rental.property.id !== propertyId));
      setDetails((current) => {
        const next = { ...current };
        delete next[propertyId];
        return next;
      });
      setExpandedId(null);
      setEditing(false);
    } catch (err) {
      window.alert(`Error deleting property: ${err.message}`);
    }
  };

  const handleEdit = () => {
    const detail = details[expandedId];
    if (!detail) return;
    const unit = detail.units?.[0] || {};
    const form = {
      description: detail.property?.description || "",
      monthly_rent: unit.monthly_rent ?? "",
      bedrooms: unit.bedrooms ?? "",
      bathrooms: unit.bathrooms ?? "",
      marketing_title: unit.marketing_title || "",
      availability_date: unit.availability_date || "",
      listing_status: unit.listing_status || "pending",
      progress: unit.progress || "submitted",
    };
    setEditForm(form);
    setEditOriginal(form);
    setEditing(true);
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditForm(null);
    setEditOriginal(null);
  };

  const handleEditSave = async (event) => {
    event.preventDefault();
    if (!editForm || !editOriginal || expandedId === null) return;

    const payload = {};
    if (editForm.description !== editOriginal.description) payload.description = editForm.description;

    const unitChanges = {};
    EDITABLE_UNIT_FIELDS.forEach((field) => {
      if (editForm[field] !== editOriginal[field]) {
        unitChanges[field] = NUMERIC_FIELDS.has(field)
          ? editForm[field] === ""
            ? null
            : Number(editForm[field])
          : editForm[field];
      }
    });
    if (Object.keys(unitChanges).length) payload.unit = unitChanges;
    if (!Object.keys(payload).length) {
      handleEditCancel();
      return;
    }

    setSaving(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/rentals/${expandedId}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let errorMessage = "Failed to update property";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `Server error (${response.status})`;
        }
        throw new Error(errorMessage);
      }
      const updatedDetail = await response.json();
      setDetails((current) => ({ ...current, [expandedId]: updatedDetail }));
      setRentals((current) =>
        current.map((rental) => {
          if (rental.property.id !== expandedId) return rental;
          const updatedUnit = updatedDetail.units?.[0];
          return {
            ...rental,
            property: updatedDetail.property,
            unit: updatedUnit || rental.unit,
            amenities: updatedDetail.amenities,
            fees: updatedUnit?.fees || [],
            utilities: updatedUnit?.utilities || null,
          };
        })
      );
      handleEditCancel();
    } catch (err) {
      window.alert(`Error updating property: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          <div className="client-list-heading">
            <div className="lc">
              <h3 className="cl-rh">Rental Properties</h3>
              <p className="cl-rh-sub">View and manage all your rental property listings.</p>
            </div>
          </div>
          <div className="client-list-table">
            <p className="loading">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          <div className="client-list-heading">
            <div className="lc">
              <h3 className="cl-rh">Rental Properties</h3>
              <p className="cl-rh-sub">View and manage all your rental property listings.</p>
            </div>
          </div>
          <div className="client-list-table">
            <p className="error">Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-list-root">
      <div className="client-list-container">
        <div className="client-list-heading">
          <div className="lc">
            <h3 className="cl-rh">Rental Properties</h3>
            <p className="cl-rh-sub">Click a property to review photos and listing details.</p>
          </div>
        </div>

        <div className="client-list-table">
          <div className="cl-table-container">
            <table className="cl-styled-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Type</th>
                  <th>Beds/Baths</th>
                  <th>Monthly Rent</th>
                  <th>Progress</th>
                  <th>Listing Status</th>
                  <th>Available Date</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rentals.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ padding: "20px", textAlign: "center" }}>
                      No rental properties found. Properties will appear here once they are created.
                    </td>
                  </tr>
                ) : (
                  rentals.map((rental) => {
                    const property = rental.property || {};
                    const address = getAddress(property);
                    const propertyId = property.id;
                    const isExpanded = expandedId === propertyId;
                    return (
                      <React.Fragment key={propertyId}>
                        <tr
                          aria-expanded={isExpanded}
                          onClick={() => handleRowClick(propertyId)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleRowClick(propertyId);
                            }
                          }}
                          style={{ cursor: "pointer" }}
                          tabIndex="0"
                        >
                          <td>
                            {address.primary}
                            <br />
                            <small style={{ color: "#666" }}>{address.secondary}</small>
                          </td>
                          <td>{property.property_type?.replace(/_/g, " ") || "N/A"}</td>
                          <td>
                            {rental.unit ? `${rental.unit.bedrooms} bed / ${rental.unit.bathrooms} bath` : "N/A"}
                          </td>
                          <td>{rental.unit ? formatCurrency(rental.unit.monthly_rent) : "N/A"}</td>
                          <td>
                            <RentalProgressIndicator progress={rental.unit?.progress} />
                          </td>
                          <td>
                            <ListingStatusBadge status={rental.unit?.listing_status} />
                          </td>
                          <td>{formatDate(rental.unit?.availability_date || property.available_date)}</td>
                          <td>{formatDate(property.created_at)}</td>
                          <td />
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan="9" style={{ padding: "10px" }}>
                              {detailLoadingId === propertyId ? (
                                <p className="loading" style={{ padding: "18px" }}>Loading property details...</p>
                              ) : detailError?.propertyId === propertyId ? (
                                <div style={{ padding: "18px" }}>
                                  <p className="error">Error: {detailError.message}</p>
                                  <button type="button" onClick={() => fetchDetail(propertyId)} style={buttonStyle}>
                                    Retry
                                  </button>
                                </div>
                              ) : details[propertyId] ? (
                                <RentalDetailPanel
                                  detail={details[propertyId]}
                                  editing={editing}
                                  editForm={editForm}
                                  saving={saving}
                                  onEdit={handleEdit}
                                  onCancel={handleEditCancel}
                                  onChange={handleEditChange}
                                  onSave={handleEditSave}
                                  onDelete={(event) => handleDelete(event, propertyId, address.primary)}
                                />
                              ) : null}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalPropertyList;
